import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { KineticLines, Overline, Reveal } from "@/components/motion";import Marquee from "@/components/Marquee";
import { IMAGES } from "@/data/demo";
import { getClubs } from "@/lib/api";
import { useSiteContent } from "@/lib/content";

const CHAPTERS = [
  {
    num: "01",
    title: "Le Stage",
    text: "Des stages de football pensés comme des expériences : éducateurs diplômés, groupes réduits, progression individuelle et cadre d'exception sur la Côte d'Azur.",
    image: "/stage-chapitre-01.webp",
  },
  {
    num: "02",
    title: "Le Match",
    text: "L'organisation de matchs amicaux professionnels et semi-professionnels : opposition de niveau, stades partenaires, logistique complète et événementiel maîtrisé.",
    image: IMAGES.night,
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const [clubs, setClubs] = useState([]);
  const c = useSiteContent();

  useEffect(() => {
    getClubs().then(setClubs);
  }, []);

  const chapters = CHAPTERS.map((ch, i) => ({
    ...ch,
    title: c(`home.ch${i + 1}.title`, ch.title),
    text: c(`home.ch${i + 1}.text`, ch.text),
  }));

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} data-testid="home-hero" className="relative h-screen min-h-[640px] overflow-hidden flex items-center">
        <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-32">
          <img src={IMAGES.heroHome} alt="Stade de football sous les projecteurs" className="w-full h-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/90 via-[#050505]/55 to-[#050505]/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse 55% 45% at 25% 70%, rgba(212,175,55,0.14), transparent)" }}
        />

        <motion.div style={{ opacity: fade }} className="relative z-10 w-full px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
            <Overline>Stages de football — Matchs amicaux pro & semi-pro</Overline>
          </motion.div>

          <h1 className="sr-only">3M Match Pro — Stages de football et matchs amicaux professionnels</h1>
          <div className="mt-6 overflow-hidden">
            <motion.img
              src="/logo-3m-full.png"
              alt="3M Match Pro — Organisateur d'événements sportifs"
              initial={{ y: "55%", opacity: 0, scale: 0.96 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-xl lg:max-w-3xl drop-shadow-[0_0_45px_rgba(212,175,55,0.22)]"
            />
          </div>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-white/75"
          >
            {c("home.hero.subtitle", "L'organisation de stages de football et de matchs amicaux de haut niveau. L'expérience du jeu, l'exigence du professionnalisme.")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/stage-payet"
              data-testid="hero-cta-stages"
              className="px-8 py-4 bg-rouge text-white text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_24px_rgba(230,0,0,0.35)]"
            >
              Stage Payet
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-contact"
              className="px-8 py-4 border-2 border-gold text-gold text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-gold hover:text-noir transition-all"
            >
              Contact
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/40 flex flex-col items-center gap-2"
        >
          <span className="text-[10px] uppercase tracking-[0.3em]">Défiler</span>
          <ChevronDown size={18} className="animate-bounce" />
        </motion.div>
      </section>

      <Marquee />

      {/* MANIFESTO */}
      <section data-testid="home-manifesto" className="px-6 md:px-12 lg:px-24 py-24 md:py-36">
        <Reveal>
          <Overline>Notre terrain de jeu</Overline>
          <h2 className="mt-4 font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tight max-w-3xl">
            Trois terrains, <span className="text-gold">une même exigence</span>
          </h2>
        </Reveal>
        <div className="mt-16 space-y-20 md:space-y-28">
          {chapters.map((ch, i) => (
            <div
              key={ch.num}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center ${i % 2 === 1 ? "" : ""}`}
            >
              <Reveal className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="relative overflow-hidden group aspect-[4/3]">
                  <img
                    src={ch.image}
                    alt={ch.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute inset-0 border border-white/10 group-hover:border-gold/50 transition-colors duration-500" />
                </div>
              </Reveal>
              <Reveal delay={0.1} className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <span className="font-display text-7xl md:text-8xl text-stroke-gold leading-none">{ch.num}</span>
                <h3 className="mt-2 font-display uppercase text-3xl md:text-4xl tracking-wide">{ch.title}</h3>
                <p className="mt-4 text-white/65 leading-relaxed max-w-lg">{ch.text}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* CLUBS — ILS NOUS FONT CONFIANCE */}
      <section data-testid="home-clubs" className="px-6 md:px-12 lg:px-24 pb-24 md:pb-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative overflow-hidden group aspect-[4/3]">
              <img
                src="/clubs-confiance.jpg"
                alt="Match amical entre clubs partenaires de 3M Match Pro"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <span className="absolute inset-0 border border-white/10 group-hover:border-gold/50 transition-colors duration-500" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <span className="font-display text-7xl md:text-8xl text-stroke-gold leading-none">03</span>
            <h3 className="mt-2 font-display uppercase text-3xl md:text-4xl tracking-wide">
              {c("home.clubs.title", "") ? (
                <span className="text-gold">{c("home.clubs.title")}</span>
              ) : (
                <>Ils nous font <span className="text-gold">confiance</span></>
              )}
            </h3>
            <p className="mt-4 text-white/65 leading-relaxed max-w-lg">
              {c("home.clubs.text", "Clubs professionnels, clubs amateurs et sélections : ils nous confient leurs stages, leurs matchs amicaux et leurs événements.")}
            </p>
            {clubs.length > 0 && (
              <ul data-testid="home-clubs-list" className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 max-w-xl">
                {clubs.map((c, i) => (
                  <li key={c.id} data-testid={`home-club-${i}`} className="flex items-center gap-2.5 text-xs md:text-sm uppercase tracking-[0.12em] text-white/70 hover:text-gold transition-colors">
                    <span className="w-1.5 h-1.5 bg-gold shrink-0" aria-hidden="true" /> {c.name}
                  </li>
                ))}
              </ul>
            )}
          </Reveal>
        </div>
      </section>

      {/* STAGE PAYET TEASER */}
      <section data-testid="home-stage-payet" className="relative py-24 md:py-36 overflow-hidden">
        <div className="px-6 md:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6">
            <Overline>Le rendez-vous des jeunes talents</Overline>
            <h2 className="mt-4 font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.88] tracking-tight">
              Stage <span className="text-gold">Payet</span>
            </h2>
            <p className="mt-6 text-white/65 leading-relaxed max-w-lg">
              Des stages de perfectionnement pour les catégories U8 à U13 : technique, motricité,
              matchs et progression individuelle, dans un cadre premium sur la Côte d'Azur.
            </p>
            <Link
              to="/stage-payet"
              data-testid="home-cta-stage-payet"
              className="mt-9 inline-flex items-center gap-3 px-8 py-4 bg-rouge text-white text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_20px_rgba(230,0,0,0.3)]"
            >
              Découvrir les stages <ArrowRight size={16} />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-4 border border-gold/25 translate-x-4 translate-y-4" aria-hidden="true" />
              <div className="relative overflow-hidden aspect-[16/11]">
                <img src="/stage-payet-teaser.webp" alt="Dimitri Payet montre un geste technique aux jeunes stagiaires" loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 to-transparent" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TENUES TEASER */}
      <section data-testid="home-tenues" className="relative py-24 md:py-36 bg-[#0a0a0a] border-y border-white/5 overflow-hidden">
        <div className="px-6 md:px-12 lg:px-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative overflow-hidden aspect-[4/5] max-w-md group">
              <img
                src="/pack-joueurs.jpeg"
                alt="Pack joueurs officiel 3M Match Pro"
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 to-transparent" />
              <span className="absolute bottom-5 left-5 text-[10px] uppercase tracking-[0.3em] text-gold font-bold">
                Pack joueurs — officiel
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6 order-1 lg:order-2">
            <Overline>L'identité sur le terrain</Overline>
            <h2 className="mt-4 font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.88] tracking-tight">
              <span className="text-gold">{c("home.boutique.title", "Boutique")}</span>
            </h2>
            <Link
              to="/boutique"
              data-testid="home-cta-boutique"
              className="mt-9 inline-flex items-center gap-3 px-8 py-4 border-2 border-gold text-gold text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-gold hover:text-noir transition-all"
            >
              Découvrir la boutique <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MATCHS TEASER */}
      <section data-testid="home-matchs" className="relative py-32 md:py-44 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.night} alt="" loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#050505]/75" />
        </div>
        <div className="relative z-10 px-6 md:px-12 lg:px-24">
          <Reveal>
            <Overline>Professionnels & semi-professionnels</Overline>
            <h2 className="mt-4 font-display uppercase text-5xl sm:text-6xl lg:text-8xl leading-[0.85] tracking-tight max-w-4xl">
              Matchs <span className="text-gold">Amicaux</span> d'exception
            </h2>
            <p className="mt-6 max-w-xl text-white/70 leading-relaxed">
              Préparation, oppositions de niveau, événements de club : nous organisons vos matchs
              amicaux de A à Z, sur des pelouses à la hauteur de vos ambitions.
            </p>
            <Link
              to="/matchs-amicaux"
              data-testid="home-cta-matchs"
              className="mt-9 inline-flex items-center gap-3 px-8 py-4 bg-rouge text-white text-sm font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0_24px_rgba(230,0,0,0.35)]"
            >
              En savoir plus <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
